/**
 * Das Etikett „setzt die vorige Nachricht fort“ und der Vermerk „abgebrochen“ an einer Blase. (12.10.2026, Bahn „fortsetzung-anhaengen“)
 *
 * Wurde eine Äußerung mitten im Satz abgeschickt und Martin sprach binnen 12 s weiter (shared/fortsetzung.ts), soll der Verlauf es sagen: die
 * zweite Nachricht steht als Anschluss an die erste da (kleines Etikett, angelegter Rand), und die Antwort, die er dafür abgebrochen hat, bleibt
 * stehen — als abgebrochen gekennzeichnet. Nichts wird gelöscht, nichts migriert.
 *
 * Das Merkmal kommt an drei Stellen an und geht an allen drei hierher (wie der Kanal-Vermerk, kanal-vermerk.ts): live aus der Sprechtaste (die
 * Seite weiß, dass die Aufnahme eine Fortsetzung war), nach dem Nachladen aus der Datenbank (`metadata.fortsetzung` / `metadata.abgebrochen`)
 * und beim Wiederaufbau aus dem lokalen Verlauf (`fortsetzung` / `abgebrochen` am Eintrag). Das Aussehen steht in frontend/style.css
 * (`.bubble--fortsetzung`, `.bubble--abgebrochen`), nicht hier.
 */

import { adressatAus, sekundaerAus, type Adressat } from "../../shared/dreier-adressat.js";
import { bilderAus, type SchmiedBild } from "../../shared/schmied-bilder.js";
import { karteBezugAus, karteIdAus } from "../../shared/schmied-karte.js";
import { ENTWICKLER_QUELLE, SCHMIED_ID, entwicklerIdAus, frageAus, rueckfrageIdAus, type BeitragFrage } from "../../shared/entwickler-im-chat.js";

export interface BlasenMerkmal {
  fortsetzung?: boolean;
  abgebrochen?: boolean;
  /** 15.10.2026 (Dreier-Modus): eine Systemzeile ist ein Beitrag des Entwicklers (`metadata.source = "entwickler"`, lib/entwickler-blase.ts). */
  entwickler?: boolean;
  /** Der Beitrag bittet um Ja/Nein (`metadata.frage`): Knöpfe unter der Blase, nur für Konto 1 (lib/frage-knoepfe.ts). */
  frage?: BeitragFrage;
  /** 20.10.2026: Martins Tipp auf Ja/Nein einer Karte — die Zeile bleibt gespeichert und gesendet, der Chat zeigt keine eigene Blase (shared/entwickler-im-chat.ts `kartenantwortGilt`). */
  kartenantwort?: true;
  /** 16.10.2026 (Dreier-Modus): an wen die Zeile geht — nur ein gültiger, gespeicherter Wert (`metadata.an`, lib/adressat-vermerk.ts). */
  an?: Adressat;
  /** Der zweite Adressat eines Beitrags (`metadata.sekundaer`), nie derselbe wie `an`. */
  sekundaer?: Adressat;
  /**
   * 16.10.2026 (Zimmermann P1): der Beitrag ist NICHT vom Schmied — die Kennung (`entwicklerIdAus`) oder ENTWICKLER_UNBEKANNT. Fehlt das
   * Feld, ist es der Schmied (Altzeilen bleiben, wie sie waren). Mit Feld: keine Ja/Nein-Knöpfe, kein Adressat, kein Vorlesen und kein
   * Mikro wie beim Schmied (app.ts) — die Blase zeigt nur seinen Namen (lib/entwickler-blase.ts `anzeigeEntwickler`).
   */
  entwicklerId?: string;
  /** 19.10.2026 (Befehlskanal P1): Kennung der Rückfrage des Zimmermanns (vom Server, 6 Hex) — Karte mit Ja/Nein, nur Anzeige (lib/rueckfrage-karte.ts). */
  rueckfrage?: string;
  /** 20.10.2026: Kennung der Ja/Nein-Karte des Schmieds (vom Server, 6 Hex, `metadata.karte`) — nur mit `frage`, nur beim Schmied (lib/frage-knoepfe.ts). */
  karte?: string;
  /** 20.10.2026: Martins Nachricht ist der Tipp auf diese Karte (`metadata.karteAntwort`, nur vom Server gesetzt) — die Kennung. */
  karteAntwort?: string;
  /** 16.10.2026: Bilder unter dem Beitrag des Schmieds (`metadata.bilder`, shared/schmied-bilder.ts) — nur gültige Adressen, nur beim Schmied. */
  bilder?: SchmiedBild[];
}

/** Steht in `entwicklerId`, wenn der Absender unbekannt ist (`entwicklerIdAus` → null). Besteht die Namensprüfung nie — bleibt also unbekannt. */
export const ENTWICKLER_UNBEKANNT = "?";

/**
 * Der Absender eines Beitrags fürs Merkmal — aus metadata, Bus-Ereignis oder lokalem Eintrag (`{ entwickler: <id> }`), mit DERSELBEN
 * Lesart wie Server und Fremd-Marke (`entwicklerIdAus`): fehlt = Schmied (nichts vermerkt), sonst die Kennung, unbekannt = „?“. Rein.
 */
export function entwicklerMerkmal(traeger: unknown): { entwicklerId?: string } {
  const id = entwicklerIdAus(traeger);
  return id === SCHMIED_ID ? {} : { entwicklerId: id ?? ENTWICKLER_UNBEKANNT };
}

/** Die Adressaten aus `metadata` oder einem Bus-Ereignis — nur gültige Werte, sonst nichts (shared/dreier-adressat.ts). Rein. */
export function adressatenAus(q: { an?: unknown; sekundaer?: unknown } | null | undefined): Pick<BlasenMerkmal, "an" | "sekundaer"> {
  const an = adressatAus(q?.an);
  const sekundaer = sekundaerAus(q?.sekundaer, an);
  return { ...(an ? { an } : {}), ...(sekundaer ? { sekundaer } : {}) };
}

/** Aus `metadata` einer Verlaufszeile: nur ein ausdrückliches `true` zählt, alles andere (fehlt, alte Zeile, falscher Typ) ist kein Merkmal. */
export function merkmalAusMetadaten(meta: unknown): BlasenMerkmal {
  if (typeof meta !== "object" || meta === null) return {};
  const m = meta as { fortsetzung?: unknown; abgebrochen?: unknown; source?: unknown; frage?: unknown; an?: unknown; sekundaer?: unknown; bilder?: unknown; rueckfrage?: unknown; kartenantwort?: unknown; karte?: unknown; karteAntwort?: unknown };
  const beitrag = m.source === ENTWICKLER_QUELLE;
  // 16.10.2026 (Zimmermann P1): Frage und Adressat eines Beitrags zählen NUR beim Schmied; ein anderer (oder unbekannter) zeigt nur Text.
  const fremd = beitrag ? entwicklerMerkmal(meta) : {};
  const schmiedFelder = !fremd.entwicklerId;
  const bilder = beitrag && schmiedFelder ? bilderAus(m.bilder) : [];
  return {
    ...(m.fortsetzung === true ? { fortsetzung: true } : {}),
    ...(m.abgebrochen === true ? { abgebrochen: true } : {}),
    // Nur an Martins Zeile an den Schmied (der Server setzt die Marke nur dort; hier nochmals streng gelesen).
    ...(!beitrag && m.kartenantwort === true && adressatAus(m.an) === "schmied" ? { kartenantwort: true as const } : {}),
    ...(beitrag ? { entwickler: true } : {}),
    ...(beitrag && schmiedFelder && frageAus(m.frage) ? { frage: frageAus(m.frage)! } : {}),
    ...karteMerkmal(beitrag ? (schmiedFelder ? SCHMIED_ID : fremd.entwicklerId ?? null) : null, m),
    ...(fremd.entwicklerId === "zimmermann" && rueckfrageIdAus(m.rueckfrage) ? { rueckfrage: rueckfrageIdAus(m.rueckfrage)! } : {}),
    ...(schmiedFelder ? adressatenAus(m) : {}),
    ...(bilder.length ? { bilder } : {}),
    ...fremd,
  };
}

/** Die Entwickler, deren Karten Martin mit Bezug beantwortet (20.10.2026, Martin 22:07: auch Zimmermann und Hüter). */
const KARTEN_ABSENDER: readonly string[] = [SCHMIED_ID, "zimmermann", "hueter"];

/**
 * 20.10.2026: Kartenkennung aus `metadata` oder einem Bus-Ereignis — am Beitrag eines Entwicklers (`karte`; beim Schmied nur mit `frage`,
 * bei Zimmermann/Hüter die Karte selbst) bzw. an Martins Nachricht an einen Entwickler (`karteAntwort.kennung`, `an` = Absender der Karte).
 * `beitragVon`: der Absender des Beitrags (SCHMIED_ID, „zimmermann“, „hueter“) — `null` für Martins Zeile. Nur gültige Werte. Rein.
 */
export function karteMerkmal(beitragVon: string | null, q: { frage?: unknown; karte?: unknown; karteAntwort?: unknown; an?: unknown; source?: unknown } | null | undefined): Pick<BlasenMerkmal, "karte" | "karteAntwort"> {
  if (beitragVon !== null) {
    if (!KARTEN_ABSENDER.includes(beitragVon)) return {};
    const id = beitragVon !== SCHMIED_ID || frageAus(q?.frage) ? karteIdAus(q?.karte) : null;
    return id ? { karte: id } : {};
  }
  const an = adressatAus(q?.an);
  if (q?.source === ENTWICKLER_QUELLE || !an || !KARTEN_ABSENDER.includes(an)) return {};
  const b = karteBezugAus(q?.karteAntwort);
  return b ? { karteAntwort: b.kennung } : {};
}

function marke(klasse: string, text: string, hinweis: string): HTMLElement {
  const el = document.createElement("span");
  el.className = klasse;
  el.textContent = text;
  el.title = hinweis;
  return el;
}

/**
 * Hängt das Etikett an die Blase — doppelt aufgerufen, steht es einmal. Das Etikett ist der erste Kindknoten (steht über dem Text);
 * der Rand und die Form kommen von der Klasse. Ohne Merkmal geschieht nichts.
 */
export function setzeFortsetzungsVermerk(blase: HTMLElement, merkmal: BlasenMerkmal | null | undefined): void {
  if (!merkmal) return;
  if (merkmal.fortsetzung === true && !blase.classList.contains("bubble--fortsetzung")) {
    blase.classList.add("bubble--fortsetzung");
    blase.prepend(
      marke("bubble-fortsetzung-marke", "setzt die vorige Nachricht fort", "Du hast weitergesprochen, kurz nachdem die vorige Nachricht abgeschickt wurde."),
    );
  }
  if (merkmal.abgebrochen === true && !blase.classList.contains("bubble--abgebrochen")) {
    blase.classList.add("bubble--abgebrochen");
    // Am Ende der Blase, vor der Zeitzeile: der Text bleibt vorn.
    const zeit = blase.querySelector<HTMLElement>(":scope > .bubble-ts");
    const m = marke("bubble-abgebrochen-marke", "abgebrochen", "Diese Antwort wurde abgebrochen, weil du weitergesprochen hast.");
    if (zeit) blase.insertBefore(m, zeit);
    else blase.append(m);
  }
}
